import { features, comments, topics, rankings } from '@/mocks/home';

export function FeaturesSection() {
  return (
    <section id="features" className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center gap-3 mb-8 md:mb-10">
          <div className="w-1 h-6 md:h-7 bg-primary-500 rounded-full"></div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">平台特色</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
          {features.map((item, i) => (
            <div key={i} className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6 hover:border-primary-200 transition-colors group cursor-default">
              <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center bg-primary-50 text-primary-600 rounded-lg mb-4 group-hover:bg-primary-100 transition-colors">
                <i className={`${item.icon} text-lg md:text-xl`}></i>
              </div>
              <h3 className="text-sm md:text-base font-semibold text-foreground-900 font-heading mb-2">{item.title}</h3>
              <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CommentsSection() {
  return (
    <section id="comments" className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center gap-3 mb-8 md:mb-10">
          <div className="w-1 h-6 md:h-7 bg-primary-500 rounded-full"></div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">吃瓜评论</h2>
          <span className="text-xs text-primary-500 bg-primary-50 px-2 py-0.5 rounded-full font-medium">精选</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {comments.map((item, i) => (
            <div key={i} className="bg-background-100 rounded-lg border border-background-200/70 p-4 md:p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 font-bold text-sm shrink-0">
                  {item.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground-900">{item.name}</div>
                  <div className="text-xs text-foreground-500">{item.time}</div>
                </div>
              </div>
              <p className="text-sm text-foreground-700 leading-relaxed mb-3">{item.content}</p>
              <div className="flex items-center gap-2 text-xs text-foreground-500">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-thumb-up-line"></i>
                </div>
                <span>{item.likes.toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TopicsSection() {
  return (
    <section id="topics" className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center gap-3 mb-8 md:mb-10">
          <div className="w-1 h-6 md:h-7 bg-primary-500 rounded-full"></div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">热门话题</h2>
          <span className="text-xs text-primary-500 bg-primary-50 px-2 py-0.5 rounded-full font-medium">实时热度</span>
        </div>

        <div className="flex flex-wrap gap-3 md:gap-4">
          {topics.map((item, i) => (
            <div
              key={i}
              className={`group flex items-center gap-2 px-4 py-2.5 rounded-full border cursor-pointer transition-colors ${
                item.hot
                  ? 'bg-primary-50 border-primary-200 hover:bg-primary-100 text-primary-700'
                  : 'bg-background-50 border-background-200/70 hover:border-background-300 text-foreground-700'
              }`}
            >
              <span className="text-sm font-medium whitespace-nowrap">{item.name}</span>
              <span className="text-xs opacity-60 whitespace-nowrap">{item.count}</span>
              {item.hot && (
                <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                  <i className="ri-fire-line text-xs"></i>
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function RankingSection() {
  const changeIcon = (c: string) => {
    if (c === 'up') return <i className="ri-arrow-up-line text-accent-500 text-xs"></i>;
    if (c === 'down') return <i className="ri-arrow-down-line text-secondary-400 text-xs"></i>;
    if (c === 'new') return <span className="text-[10px] text-accent-500 font-bold">NEW</span>;
    return <span className="w-3"></span>;
  };

  return (
    <section id="ranking" className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center gap-3 mb-8 md:mb-10">
          <div className="w-1 h-6 md:h-7 bg-primary-500 rounded-full"></div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">热度榜单</h2>
          <span className="text-xs text-accent-500 bg-accent-50 px-2 py-0.5 rounded-full font-medium">TOP 7</span>
        </div>

        <div className="bg-background-100 rounded-lg border border-background-200/70 overflow-hidden">
          {rankings.map((item, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 md:gap-4 px-4 md:px-6 py-3.5 md:py-4 cursor-pointer hover:bg-background-50 transition-colors ${
                i < rankings.length - 1 ? 'border-b border-background-200/70' : ''
              }`}
            >
              <div className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-md font-bold text-sm shrink-0 ${
                item.rank === 1 ? 'bg-primary-500 text-white' :
                item.rank === 2 ? 'bg-accent-500 text-white' :
                item.rank === 3 ? 'bg-secondary-500 text-white' :
                'bg-background-200 text-foreground-600'
              }`}>
                {item.rank}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium text-foreground-900 truncate">{item.title}</h3>
                  {item.tag && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${
                      item.tag === '爆' ? 'bg-primary-500 text-white' :
                      item.tag === '热' ? 'bg-accent-500 text-white' :
                      'bg-accent-100 text-accent-700'
                    }`}>{item.tag}</span>
                  )}
                </div>
                <span className="text-xs text-foreground-500">{item.heat} 热度</span>
              </div>

              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                {changeIcon(item.change)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}